import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "reducers";
import { IService } from "../../domain/IService";
import { IInput } from "Dashboard/scenes/Consumables/domain/IInput";
import { resourcesServices } from "../../services/resource.service";
import { IConsumableItem } from "../../domain/IConsumableItem";
import { consumablesService } from "Dashboard/scenes/Consumables/services/consumables.service";
import { IPopoverState } from "Dashboard/scenes/Carnets/domain/IPopoverState";
import { IUpdateService } from "../../domain/IUpdateService";
import { IDeleteServiceOrRepair } from "../../domain/IDeleteServiceOrRepair";

export const getConsumables = createAsyncThunk(
  "Resources/getConsumables",
  async () => {
    try {
      const consumablesResponse = await consumablesService.getInputs(
        {
          categories: [
            "LUBRICANT",
            "FILTER",
            "REPLACEMENT_PART",
            "TYRE",
            "SERVICE",
            "OTHER",
          ],
        },
        { fetchMode: 'replace' },
        new AbortController().signal
      );
      return consumablesResponse.inputs;
    } catch (err) {
      setError(err);
    }
  }
);

export const getServices = createAsyncThunk(
  "Resources/getServices",
  async (resourceId: number) => {
    try {
      return await resourcesServices.getServices(resourceId);
    } catch (err) {
      setError(err);
    }
  }
);

export const updateService = createAsyncThunk(
  "ConcreteBase/updateService",
  async (data: IUpdateService, { rejectWithValue }) => {
    try {
      return await resourcesServices.updateService(data);
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

export const deleteServiceOrRepair = createAsyncThunk(
  "ConcreteBase/deleteServiceOrRepair",
  async (data: IDeleteServiceOrRepair, { rejectWithValue }) => {
    try {
      const updatedResourceDetails = await resourcesServices.deleteServiceOrRepair(data);
      return { request: data, resourceDetails: updatedResourceDetails };
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

interface serviceAndRepairState {
  serviceRepairPopover: IPopoverState;
  loading: boolean;
  services: IService[];
  serviceOrRepairToDelete: IService | undefined;
  totalKilometers: number;
  totalHours: number;
  totalLiters: number;
  allConsumables: IInput[];
  consumables: IInput[];
  consumablesLoading: boolean;
  consumablesMap: { [key in number | string]: number | undefined };
  error: any;
}

const initialState: serviceAndRepairState = {
  serviceRepairPopover: { isOpen: false },
  loading: false,
  services: [],
  serviceOrRepairToDelete: undefined,
  totalHours: 0,
  totalKilometers: 0,
  totalLiters: 0,
  allConsumables: [],
  consumables: [],
  consumablesLoading: false,
  consumablesMap: {},
  error: null,
};

export const serviceAndRepairSlice = createSlice({
  name: "resources-serviceAndRepair",
  initialState: initialState,
  reducers: {
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    setServiceRepairPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.serviceRepairPopover = action.payload;
    },
    setConsumables: (state, action: PayloadAction<IInput[]>) => {
      state.consumables = action.payload;
    },
    setConsumablesMap: (state, action: PayloadAction<IConsumableItem>) => {
      const option = action.payload;
      if (option.quantityAmount) {
        state.consumablesMap[option.consumableInputId] = option.quantityAmount;
      } else {
        delete state.consumablesMap[option.consumableInputId];
      }
    },
    clearConsumablesMap: (state) => {
      state.consumablesMap = {};
    },
    updateServiceTemporarily: (state, action: PayloadAction<IUpdateService>) => {
      for (let serviceIndex = 0; serviceIndex < state.services.length; serviceIndex++) {
        if (state.services[serviceIndex].serviceId === action.payload.serviceId) {
          if (action.payload.note !== undefined) {
            state.services[serviceIndex].note = action.payload.note;
          }
        }
      }
    },
    requestDeletingServiceOrRepair: (state, action: PayloadAction<IService | undefined>) => {
      state.serviceOrRepairToDelete = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getConsumables.pending, (state) => {
      state.consumablesLoading = true;
    });
    builder.addCase(getConsumables.fulfilled, (state, action) => {
      state.consumablesLoading = false;
      state.consumables = action.payload ? action.payload : [];
      state.allConsumables = action.payload ? action.payload : [];
    });
    builder.addCase(getConsumables.rejected, (state, action) => {
      state.consumablesLoading = false;
      state.error = action.error;
    });
    builder.addCase(getServices.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getServices.fulfilled, (state, action) => {
      state.loading = false;
      state.services = action.payload?.services ? action.payload.services : [];
      state.totalHours = action.payload?.totalHours ? action.payload.totalHours : 0;
      state.totalKilometers = action.payload?.totalKilometers ? action.payload.totalKilometers : 0;
      state.totalLiters = action.payload?.totalLiters ? action.payload.totalLiters : 0;

    });
    builder.addCase(getServices.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
    builder.addCase(updateService.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateService.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        state.services = state.services.map(oldService => {
          if (oldService.serviceId === action.payload.serviceId) {
            return action.payload;
          } else {
            return oldService;
          }
        });
      }
    });
    builder.addCase(updateService.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
    builder.addCase(deleteServiceOrRepair.fulfilled, (state, action) => {
      if (action.payload) {
        state.services = state.services.filter(serviceOrRepair => serviceOrRepair.serviceId !== action.payload.request.maintenanceId);
      }
    });
  },
});

export const {
  setError,
  setServiceRepairPopover,
  setConsumables,
  setConsumablesMap,
  clearConsumablesMap,
  updateServiceTemporarily,
  requestDeletingServiceOrRepair,
} = serviceAndRepairSlice.actions;

export const selectState = (state: RootState) =>
  state.resources.serviceAndRepair;
export default serviceAndRepairSlice.reducer;
